import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface AutocompleteOption {
  id: string;
  label: string;
  subtitle?: string;
  disabled?: boolean;
}

interface AutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: AutocompleteOption | null) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  loading?: boolean;
  icon?: React.ReactNode;
  onFocus?: () => void;
  autoFocus?: boolean;
  className?: string;
  loadingText?: string;
  noResultsText?: string;
  noOptionsText?: string;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  options,
  placeholder = 'Start typing to search...',
  label,
  required = false,
  disabled = false,
  searchValue,
  onSearchChange,
  loading = false,
  icon,
  onFocus,
  autoFocus = false,
  className = '',
  loadingText = 'Loading...',
  noResultsText = 'No results found',
  noOptionsText = 'No options available'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Open the list immediately when the picker is revealed (e.g. Add on a card).
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  // Filter options based on search value
  // If searchValue is empty, show all options; otherwise filter
  const filteredOptions = searchValue.trim() === ''
    ? options
    : options.filter(option =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.subtitle?.toLowerCase().includes(searchValue.toLowerCase())
      );

  // Find selected option
  const selectedOption = options.find(opt => opt.id === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onSearchChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
    
    // Clear selection if input doesn't match selected option
    if (selectedOption && newValue !== selectedOption.label) {
      onSelect(null);
      onChange('');
    }
  };

  const handleSelect = (option: AutocompleteOption) => {
    if (option.disabled) {
      return;
    }
    onSelect(option);
    onChange(option.id);
    onSearchChange(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onSelect(null);
    onChange('');
    onSearchChange('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => {
        for (let i = prev + 1; i < filteredOptions.length; i++) {
          if (!filteredOptions[i].disabled) {
            return i;
          }
        }
        return prev;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => {
        for (let i = prev - 1; i >= 0; i--) {
          if (!filteredOptions[i].disabled) {
            return i;
          }
        }
        return -1;
      });
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            if (onFocus) {
              onFocus();
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full ${icon ? 'pl-10' : 'pl-3'} pr-10 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-secondary-text)]"></div>
          )}
          {!loading && searchValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] rounded"
              tabIndex={-1}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      
      {isOpen && loading && (
        <div className="absolute z-50 w-full mt-1 border border-[var(--color-border)] rounded-md shadow-lg bg-[var(--color-surface-bg)]">
          <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">{loadingText}</div>
        </div>
      )}
      
      {isOpen && !loading && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 border border-[var(--color-border)] rounded-md shadow-lg max-h-60 overflow-auto bg-[var(--color-surface-bg)]">
          {filteredOptions.map((option, index) => (
            <div
              key={option.id}
              onClick={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-sm ${
                option.disabled
                  ? 'opacity-50 cursor-not-allowed text-[var(--color-secondary-text)]'
                  : index === highlightedIndex || option.id === value
                    ? 'cursor-pointer bg-[var(--color-hover-bg)] text-[var(--color-surface-text)]'
                    : 'cursor-pointer text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]'
              }`}
            >
              <div className="font-medium">{option.label}</div>
              {option.subtitle && (
                <div className="text-xs text-[var(--color-secondary-text)] mt-0.5">{option.subtitle}</div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isOpen && !loading && searchValue.trim() !== '' && filteredOptions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 border border-[var(--color-border)] rounded-md shadow-lg bg-[var(--color-surface-bg)]">
          <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">{noResultsText}</div>
        </div>
      )}
      
      {isOpen && !loading && searchValue.trim() === '' && options.length === 0 && (
        <div className="absolute z-50 w-full mt-1 border border-[var(--color-border)] rounded-md shadow-lg bg-[var(--color-surface-bg)]">
          <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">{noOptionsText}</div>
        </div>
      )}
    </div>
  );
};

